import { DataResult, process, State } from '@progress/kendo-data-query';
import { useEffect, useState } from 'react';

interface UseKendoGridProps {
    source: any[] | DataResult | null | undefined;
    transform?: (data: any) => any;
    runTransformOnResultChange?: boolean;
    usedForNoPaginatingSource?: boolean;
}

const useKendoGrid = (
    { source, transform, runTransformOnResultChange = false, usedForNoPaginatingSource = false }: UseKendoGridProps,
    dependencies: any[] = [],
) => {
    const lengthFilter = getLength(source);

    const [gridState, setGridState] = useState<State>({
        take: usedForNoPaginatingSource ? lengthFilter : 50,
        skip: 0,
    });
    const [gridResult, setGridResult] = useState<DataResult>({
        data: [],
        total: 0,
    });

    useEffect(() => {
        if (source) {
            const result = updateGridResult(source, transform);
            setGridResult(process(result.data, gridState));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source, ...dependencies]);

    const changeGridResult = (result: any[] | DataResult, runTransform = runTransformOnResultChange || false) => {
        const length = getLength(source);
        if (runTransform) {
            setGridResult(updateGridResult(result, transform, length));
        } else {
            setGridResult(updateGridResult(result, undefined, length));
        }
    };

    return { gridState, gridResult, setGridState, changeGridResult };
};

export default useKendoGrid;

const updateGridResult = (result: any[] | DataResult, transform?: (data: any) => any, sourceLength?: number) => {
    if (isDataResult(result)) {
        return {
            data: transform ? result.data?.map((item) => transform(item)) : result.data,
            total: result.total,
        } as DataResult;
    }

    return {
        data: transform ? result.map((item) => transform(item)) : result,
        total: sourceLength ?? result.length,
    };
};

const isDataResult = (data: any[] | DataResult): data is DataResult => {
    return (data as DataResult).data !== undefined;
};

const getLength = (source: any) => {
    if (!source) {
        return 0;
    }
    if (source.hasOwnProperty('total')) {
        return source.total;
    }
    return source.length;
};